'use client';

import classNames from 'classnames';

import useId from '../../../../hooks/useId/useId';
import dataAttributes from '../../../../utils/attributes/attributes';
import { Badge } from '../../../badge/components/Badge/Badge';
import { Icon } from '../../../icon/components/Icon/Icon';
import { Typography } from '../../../typography/components/Typography/Typography';

export interface TabProps {
   id?: string;
   label: string;
   badge?: string;
   leadingIcon?: string;
   trailingIcon?: string;
   active?: boolean;
   height?: string;
   width?: string;
   tabStyle?: 'simple' | 'classic';
   onClick?: () => void;
   attributes?: Record<string, string>;
}

export const Tab = ({ id, active = false, height, width, onClick, label, badge, leadingIcon, trailingIcon, tabStyle = 'simple', attributes }: TabProps) => {
   const elementId = useId(id, 'tab');

   return (
      <button
         key={label}
         {...dataAttributes(attributes)}
         data-testid={`tab-${label}${active ? '--active' : ''}`}
         id={elementId}
         onClick={onClick}
         type="button"
         role="tab"
         style={{
            height,
            width,
            boxShadow: tabStyle === 'simple' && active ? 'inset 0 calc(-1 * var(--component-navigation-tab-container-border-width-active)) 0 var(--component-navigation-tab-container-color-border-active)' : undefined
         }}
         className={classNames(
            `hover:bg-component-navigation-tab-state-layer-color-background-active-hover focus-visible:bg-component-navigation-tab-state-layer-color-background-active-focused active:bg-component-navigation-tab-state-layer-color-background-active-pressed disabled:hover:bg-component-navigation-tab-state-layer-color-background-inactive-hover disabled:focus-visible:bg-component-navigation-tab-state-layer-color-background-inactive-focused disabled:active:bg-component-navigation-tab-state-layer-color-background-inactive-pressed z-10 appearance-none transition-colors`,
            active ? 'border-b-component-navigation-tab-container-border-width-active border-b-component-navigation-tab-container-color-border-active' : 'border-b-component-navigation-tab-container-border-width-inactive border-b-component-navigation-tab-container-color-border-inactive',
            leadingIcon ? 'pl-component-navigation-tab-container-spacing-padding-x-icon' : 'pl-component-navigation-tab-container-spacing-padding-x-no-icon',
            trailingIcon ? 'pr-component-navigation-tab-container-spacing-padding-x-icon' : 'pr-component-navigation-tab-container-spacing-padding-x-no-icon',
            { 'bg-transparent': tabStyle === 'simple' },
            { 'border border-b-0 rounded-border-tab border-component-tab-color-border': tabStyle === 'classic' },
            { 'bg-component-tab-color-background-active': tabStyle === 'classic' && active },
            { 'bg-component-tab-color-background-inactive': tabStyle === 'classic' && !active }
         )}
      >
         <div style={{ height, width }} className="min-h-component-navigation-tab-container-sizing-min-height py-component-navigation-tab-container-spacing-padding-y gap-component-navigation-tab-container-spacing-gap box-border flex h-full items-center justify-center">
            {leadingIcon && (
               <div data-testid={`tab-${label}-leading-icon`} className="text-component-navigation-tab-icon-color-fill">
                  <Icon icon={leadingIcon} />
               </div>
            )}
            <div className="text-component-navigation-tab-label-color-text truncate">
               <Typography color="inherit" whiteSpace="nowrap">
                  {label}
               </Typography>
            </div>
            {trailingIcon && (
               <div data-testid={`tab-${label}-trailing-icon`} className="text-component-navigation-tab-icon-color-fill">
                  <Icon icon={trailingIcon} />
               </div>
            )}
            {badge && <Badge variant={active ? 'primary' : 'neutral'}>{badge}</Badge>}
         </div>
      </button>
   );
};
